/**
 * FileLinkPage — the page behind a permanent project-file link (ADR-210).
 *
 *   /files/<scope>/<path>
 *
 * Mission Control's session is a Bearer token the SPA holds, so a bare link
 * carries no credential: this page, already behind the auth guard, asks
 * GET /api/files/resolve for the file's facts and a fresh 15-minute signed URL.
 * Never public (OD-19.2), never a fuzzy search (OD-19.3): a path that no longer
 * exists says so, in the words the owner chose.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  absoluteLink,
  copyToClipboard,
  resolveFileLink,
  viewerFor,
  type ResolveResult,
  type ResolvedFile,
} from '../api/fileLinks'

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return ''
  if (n < 1024) return `${n} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let v = n / 1024
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`
}

type State = { phase: 'loading' } | { phase: 'done'; result: ResolveResult }

export function FileLinkPage() {
  const params = useParams<{ scope: string; '*': string }>()
  const scope = params.scope ?? ''
  const path = params['*'] ?? ''
  const [state, setState] = useState<State>({ phase: 'loading' })
  const [copy, setCopy] = useState<'idle' | 'copied' | 'manual'>('idle')
  const [manualUrl, setManualUrl] = useState('')
  const refreshed = useRef(false)

  useEffect(() => {
    let cancelled = false
    refreshed.current = false
    setState({ phase: 'loading' })
    setCopy('idle')
    void resolveFileLink(scope, path).then((result) => {
      if (!cancelled) setState({ phase: 'done', result })
    })
    return () => {
      cancelled = true
    }
  }, [scope, path])

  // The signed URL lasts 15 minutes. A player left open past that fails on the next
  // range request; ask once for a fresh URL rather than leave a dead player.
  const onMediaError = useCallback(() => {
    if (refreshed.current) return
    refreshed.current = true
    void resolveFileLink(scope, path).then((result) => setState({ phase: 'done', result }))
  }, [scope, path])

  const onCopy = useCallback(async (file: ResolvedFile) => {
    const url = absoluteLink(file.permanent_path)
    const r = await copyToClipboard(url)
    if (r.copied) {
      setCopy('copied')
    } else {
      setManualUrl(url)
      setCopy('manual')
    }
  }, [])

  if (state.phase === 'loading') {
    return <div className="text-sm text-gray-400 animate-pulse">Opening file...</div>
  }

  const { result } = state

  if (result.kind === 'not_found') {
    return (
      <div role="alert" className="max-w-2xl rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <p className="font-semibold">File not found</p>
        <p className="mt-1">{result.message || `This file no longer exists at ${path}. It may have been renamed, moved or deleted.`}</p>
      </div>
    )
  }

  if (result.kind === 'error') {
    return (
      <div role="alert" className="max-w-2xl rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {result.message}
      </div>
    )
  }

  const file = result.file
  const viewer = viewerFor(file)

  return (
    <div className="max-w-4xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 break-words">{file.name}</h1>
        <p className="mt-1 text-sm text-gray-500 break-all" data-testid="file-path">{file.path}</p>
        <p className="mt-1 text-xs text-gray-400">
          {formatBytes(file.size)}
          {file.media_type ? ` · ${file.media_type}` : ''}
        </p>
      </div>

      {viewer === 'video' && (
        <video src={file.signed_url} controls preload="metadata" onError={onMediaError} className="w-full rounded-lg bg-black" />
      )}
      {viewer === 'image' && (
        <img src={file.signed_url} alt={file.name} onError={onMediaError} className="max-w-full rounded-lg border border-gray-200" />
      )}
      {viewer === 'audio' && <audio src={file.signed_url} controls onError={onMediaError} className="w-full" />}
      {viewer === 'download' && (
        <p className="text-sm text-gray-600">This kind of file cannot be previewed here. Use Download to open it.</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <a
          href={file.download_url}
          className="inline-flex items-center rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50"
        >
          Download
        </a>
        <button
          type="button"
          onClick={() => void onCopy(file)}
          className="inline-flex items-center rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
        >
          Copy link
        </button>
        {copy === 'copied' && <span role="status" className="text-sm text-green-700">Link copied</span>}
      </div>

      {copy === 'manual' && (
        <div className="space-y-1">
          <p role="status" className="text-sm text-gray-600">Your browser did not allow copying. Select the link below and copy it yourself.</p>
          <input
            readOnly
            aria-label="Permanent link"
            value={manualUrl}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded border border-gray-300 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-800"
          />
        </div>
      )}
      <p className="text-xs text-gray-400">
        This link works only for people signed in to this Mission Control. If the file is renamed or moved the link stops working.
      </p>
    </div>
  )
}
